import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, MapPin, Users, Plane, Plus, DollarSign, Check, Loader2, Target, Camera, X } from "lucide-react";

const TYPES = [
  { key: "all", label: "All" },
  { key: "open_play", label: "Open Play" },
  { key: "clinic", label: "Clinic" },
  { key: "league", label: "League" },
  { key: "tournament", label: "Tournament" },
  { key: "pickup", label: "Pickup" },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = ["6am","7am","8am","9am","10am","11am","12pm","1pm","2pm","3pm","4pm","5pm","6pm","7pm","8pm","9pm"];
const REVIEW_TYPES = new Set(["clinic", "league", "tournament"]);

const TYPE_STYLES = {
  open_play: "bg-[#BED445] text-black",
  pickup: "bg-[#4A90E2]/15 text-[#4A90E2]",
  clinic: "bg-[#005F19]/12 text-[#005F19]",
  league: "bg-[#0E395F]/12 text-[#0E395F]",
  tournament: "bg-[#E4572E]/12 text-[#E4572E]",
};

function EventCard({ e, onRsvp, busy }) {
  const full = e.spots_left === 0 && !e.rsvped && !e.is_host;
  return (
    <div className="min-w-[280px] max-w-[280px] snap-start rounded-2xl bg-white border border-black/5 shadow-sm overflow-hidden flex flex-col" data-testid={`event-card-${e.event_id}`}>
      {e.photo_url ? (
        <img src={e.photo_url} alt={e.title} className="h-28 w-full object-cover" />
      ) : (
        <div className="h-28 w-full bg-[#0E395F]/5 grid place-items-center text-black/15"><CalendarDays size={30} /></div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide ${TYPE_STYLES[e.type] || "bg-black/5 text-black/60"}`}>{e.type_label}</span>
          {e.price ? <span className="rounded-full px-2 py-0.5 text-[11px] font-bold bg-black/5 text-black/70 inline-flex items-center gap-0.5"><DollarSign size={10} />{e.price}</span> : null}
        </div>
        <div className="font-black pv-text-dark mt-2 leading-tight line-clamp-2">{e.title}</div>
        <div className="mt-2 space-y-1 text-xs text-black/55">
          {(e.day || e.time) && <div className="flex items-center gap-1.5"><CalendarDays size={12} />{[e.day, e.time].filter(Boolean).join(" · ")}{e.duration ? ` · ${e.duration}` : ""}</div>}
          <div className="flex items-center gap-1.5 truncate"><MapPin size={12} />{e.court_name || e.city || "TBD"}{typeof e.distance_mi === "number" ? ` · ${e.distance_mi} mi` : ""}</div>
          <div className="flex items-center gap-1.5"><Users size={12} />{e.rsvp_count} going · {e.spots_left} left</div>
          {(e.skill_min || e.skill_max) && <div className="flex items-center gap-1.5"><Target size={12} />DUPR {e.skill_min ?? "—"}–{e.skill_max ?? "—"}</div>}
        </div>
        <div className="flex items-center gap-2 mt-3 pt-1">
          <Avatar className="h-6 w-6"><AvatarImage src={e.host_avatar} /><AvatarFallback>{e.host_name?.[0]}</AvatarFallback></Avatar>
          <span className="text-[11px] text-black/45 truncate">{e.is_host ? "You're hosting" : `Host · ${e.host_name}`}</span>
        </div>
        <button
          data-testid={`rsvp-btn-${e.event_id}`}
          disabled={busy || full || e.is_host}
          onClick={() => onRsvp(e)}
          className={`mt-3 w-full rounded-full py-2 text-sm font-bold transition-all disabled:opacity-50 ${e.rsvped ? "bg-[#005F19] text-white" : "pv-bg-primary text-black hover:opacity-90"}`}
        >
          {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : e.is_host ? "Your event" : e.rsvped ? (<span className="inline-flex items-center gap-1"><Check size={14} /> Going</span>) : full ? "Full" : "RSVP"}
        </button>
      </div>
    </div>
  );
}

export default function WhatsOn() {
  const [events, setEvents] = useState(null);
  const [type, setType] = useState("all");
  const [day, setDay] = useState("all");
  const [skillFit, setSkillFit] = useState(false);
  const [place, setPlace] = useState("");
  const [placeLabel, setPlaceLabel] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showHost, setShowHost] = useState(false);

  const load = async (opts = {}) => {
    const params = {};
    const t = opts.type ?? type;
    const d = opts.day ?? day;
    const sf = opts.skillFit ?? skillFit;
    const pl = opts.place ?? placeLabel;
    if (t && t !== "all") params.type = t;
    if (d && d !== "all") params.day = d;
    if (sf) params.skill_fit = true;
    if (pl) params.place = pl;
    try {
      const { data } = await http.get("/events", { params });
      setEvents(data.events);
      setPlaceLabel(data.place || null);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, day, skillFit]);

  const searchTravel = async () => {
    if (!place.trim()) return;
    await load({ place: place.trim() });
  };
  const clearTravel = async () => { setPlace(""); await load({ place: null }); setPlaceLabel(null); };

  const rsvp = async (e) => {
    setBusy(e.event_id);
    try {
      const { data } = await http.post(`/events/${e.event_id}/rsvp`);
      setEvents((prev) => prev.map((x) => (x.event_id === data.event_id ? data : x)));
      toast.success(data.rsvped ? "You're in — added to your schedule." : "RSVP cancelled.");
    } catch (err) { toast.error(formatError(err)); }
    setBusy(null);
  };

  return (
    <section data-testid="whats-on-section">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-black font-display pv-text-dark flex items-center gap-2"><CalendarDays size={18} className="pv-text-support" /> What's On</h2>
          <p className="text-xs text-black/45">Clinics, open play, leagues & more near you</p>
        </div>
        <Button data-testid="host-event-btn" onClick={() => setShowHost(true)} className="pv-bg-dark text-white rounded-full font-bold hover:opacity-90 shrink-0"><Plus size={16} className="mr-1" /> Host</Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2">
        {TYPES.map((t) => (
          <button key={t.key} data-testid={`filter-type-${t.key}`} onClick={() => setType(t.key)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition-all ${type === t.key ? "pv-bg-dark text-white" : "bg-white border border-black/10 text-black/60"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-1">
        <Select value={day} onValueChange={setDay}>
          <SelectTrigger data-testid="filter-day" className="h-8 w-[130px] rounded-full text-xs"><SelectValue placeholder="Any day" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any day</SelectItem>
            {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid="filter-skillfit" onClick={() => setSkillFit((v) => !v)}
          className={`rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 transition-all ${skillFit ? "bg-[#005F19] text-white" : "bg-white border border-black/10 text-black/60"}`}>
          <Target size={12} /> My level
        </button>
        <div className="flex items-center gap-1 ml-auto">
          <div className="relative">
            <Plane size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-black/30" />
            <input data-testid="travel-input" value={place} onChange={(e) => setPlace(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchTravel()}
              placeholder="Traveling? City…" className="h-8 w-[150px] rounded-full border border-black/10 pl-7 pr-3 text-xs outline-none focus:border-[#0E395F]" />
          </div>
          <Button data-testid="travel-search-btn" onClick={searchTravel} size="sm" className="h-8 rounded-full pv-bg-primary text-black text-xs font-bold">Go</Button>
        </div>
      </div>
      {placeLabel && (
        <div data-testid="travel-active" className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#4A90E2]/10 text-[#4A90E2] text-xs font-bold px-3 py-1">
          Near {placeLabel} <button onClick={clearTravel} className="hover:opacity-70"><X size={12} /></button>
        </div>
      )}

      {/* Feed */}
      <div className="mt-3">
        {!events ? (
          <div className="h-40 grid place-items-center"><Loader2 className="animate-spin pv-text-dark" /></div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl bg-white border border-black/5 p-6 text-sm text-black/50 text-center" data-testid="whats-on-empty">
            No events match. Try clearing filters or host one yourself.
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto snap-x pb-2 no-scrollbar" data-testid="whats-on-feed">
            {events.map((e) => <EventCard key={e.event_id} e={e} onRsvp={rsvp} busy={busy === e.event_id} />)}
          </div>
        )}
      </div>

      <HostEventDialog open={showHost} onClose={() => setShowHost(false)} onCreated={() => { setShowHost(false); load(); }} />
    </section>
  );
}

function HostEventDialog({ open, onClose, onCreated }) {
  const empty = { type: "open_play", title: "", description: "", court_id: "", city: "", day: "", time: "", duration: "", capacity: "8", skill_min: "", skill_max: "", price: "", photo_url: "" };
  const [form, setForm] = useState(empty);
  const [courts, setCourts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const needsReview = REVIEW_TYPES.has(form.type);

  useEffect(() => {
    if (open) {
      setForm(empty);
      http.get("/courts").then((r) => setCourts(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [open]);

  const pickPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/events/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", data.url);
    } catch (e) { toast.error(formatError(e)); }
    setUploading(false);
  };

  const submit = async () => {
    if (form.title.trim().length < 2) { toast.error("Give your event a title."); return; }
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim(),
        court_id: form.court_id || null,
        city: form.city.trim() || null,
        day: form.day || null,
        time: form.time || null,
        duration: form.duration.trim() || null,
        capacity: parseInt(form.capacity, 10) || 8,
        skill_min: form.skill_min ? parseFloat(form.skill_min) : null,
        skill_max: form.skill_max ? parseFloat(form.skill_max) : null,
        price: form.type === "clinic" && form.price ? parseFloat(form.price) : null,
        photo_url: form.photo_url || null,
      };
      const { data } = await http.post("/events", payload);
      toast.success(data.status === "pending" ? "Submitted for admin review — we'll ping you when it's live." : "Event published! 🎉");
      onCreated();
    } catch (e) { toast.error(formatError(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="host-event-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Host an event</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Type</label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="host-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open_play">Open Play</SelectItem>
                <SelectItem value="pickup">Pickup</SelectItem>
                <SelectItem value="clinic">Clinic</SelectItem>
                <SelectItem value="league">League</SelectItem>
                <SelectItem value="tournament">Tournament</SelectItem>
              </SelectContent>
            </Select>
            {needsReview && <div data-testid="review-note" className="text-[11px] text-[#E4572E] mt-1">{form.type[0].toUpperCase() + form.type.slice(1)}s are reviewed by an admin before going live.</div>}
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Title</label>
            <input data-testid="host-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="w-full border rounded-xl p-2.5 text-sm mt-1" placeholder="e.g. Saturday Morning Clinic" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Description</label>
            <textarea data-testid="host-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full border rounded-xl p-2.5 text-sm mt-1" placeholder="What should players expect?" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Court (optional)</label>
            <Select value={form.court_id} onValueChange={(v) => set("court_id", v)}>
              <SelectTrigger data-testid="host-court-select"><SelectValue placeholder="Pick a court" /></SelectTrigger>
              <SelectContent>{courts.map((c) => <SelectItem key={c.court_id} value={c.court_id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {!form.court_id && (
              <input data-testid="host-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="w-full border rounded-xl p-2.5 text-sm mt-2" placeholder="Or a city (e.g. Austin, TX)" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Day</label>
              <Select value={form.day} onValueChange={(v) => set("day", v)}>
                <SelectTrigger data-testid="host-day"><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Time</label>
              <Select value={form.time} onValueChange={(v) => set("time", v)}>
                <SelectTrigger data-testid="host-time"><SelectValue placeholder="Time" /></SelectTrigger>
                <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Capacity</label>
              <input data-testid="host-capacity" type="number" min="2" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="w-full border rounded-xl p-2.5 text-sm mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">DUPR min</label>
              <input data-testid="host-skill-min" type="number" step="0.1" value={form.skill_min} onChange={(e) => set("skill_min", e.target.value)} className="w-full border rounded-xl p-2.5 text-sm mt-1" placeholder="3.0" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">DUPR max</label>
              <input data-testid="host-skill-max" type="number" step="0.1" value={form.skill_max} onChange={(e) => set("skill_max", e.target.value)} className="w-full border rounded-xl p-2.5 text-sm mt-1" placeholder="4.5" />
            </div>
          </div>
          {form.type === "clinic" && (
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Price (display only)</label>
              <input data-testid="host-price" type="number" step="1" value={form.price} onChange={(e) => set("price", e.target.value)} className="w-full border rounded-xl p-2.5 text-sm mt-1" placeholder="e.g. 25" />
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Photo (optional)</label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickPhoto(e.target.files?.[0])} data-testid="host-photo-input" />
            <div className="mt-1 flex items-center gap-3">
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full">
                {uploading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Camera size={14} className="mr-1" />} {form.photo_url ? "Change" : "Add photo"}
              </Button>
              {form.photo_url && <img src={form.photo_url} alt="preview" className="h-12 w-16 object-cover rounded-lg" />}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="host-submit-btn" onClick={submit} disabled={saving} className="pv-bg-primary text-black font-bold hover:opacity-90">
            {saving ? <Loader2 size={15} className="animate-spin mr-1" /> : null}{needsReview ? "Submit for review" : "Publish event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
